/**
 * Advisory Stage Derivation
 *
 * The two pure derivation functions the whole Strategic Advisory data layer
 * rests on:
 *
 * - `deriveChannelState` — per-channel health, reused by all three listener
 *   kinds (`engagements`, `goal`, `keyQuestions`).
 * - `deriveAdvisoryStage` — the goal channel's stage, composed on top of
 *   `deriveChannelState`'s output.
 *
 * This module is deliberately pure: no import from React, Redux, Redux
 * Toolkit, the Firestore SDK, or any service. It takes typed inputs and
 * returns typed outputs, nothing else. TypeScript is pinned at
 * 4.9.5 on this repo, so XState v5 (which requires TypeScript >= 5.0) is
 * unavailable — this is plain `switch`/`if` control flow by necessity, not
 * merely by style preference.
 */

import { UNKNOWN_ADVISORY_ERROR_CODE } from './advisoryErrorCodes';
import type {
  AdvisoryChannelStatus,
  AdvisoryStage,
  AnalysisDoc,
  ChannelState,
  CompanyFact,
  CounterpartiesFact,
  CounterpartyProfileRow,
  LiveCounterpartyProfiles,
  StrategicAdvisoryDoc,
} from './advisoryTypes';
import { WIRING_PLACEHOLDER_IDS } from './advisoryWiringPlaceholders';

/**
 * The single named constant for what "denied" means at the channel-status
 * level. `deriveChannelState` is the one place in the codebase that reads
 * this constant to decide `'denied'` — no other module should ever re-type
 * the `'permission-denied'` literal to make that same decision.
 */
const PERMISSION_DENIED_ERROR_CODE = 'permission-denied';

/**
 * Derives one channel's health from its raw status. This function is
 * generic over every listener kind, including `engagements`, and takes NO
 * `kind` parameter at all. That absence is what makes it reusable across
 * all three kinds and what makes a global `loading` or `error` field on
 * the advisory slice unnecessary — health lives per-channel, by
 * construction, everywhere this function is called.
 *
 * Evaluation order is load-bearing:
 * 1. `errorCode === 'permission-denied'` -> `'denied'`. This is the single
 *    place in the codebase that defines what "denied" means — any other
 *    module that needs to know whether a channel was denied must call this
 *    function, never re-derive the answer itself.
 * 2. any other non-null `errorCode` -> `'failed'`.
 * 3. `!firstSnapshotReceived` -> `'loading'`.
 * 4. `isEmpty` -> `'empty'`, otherwise `'ready'`.
 *
 * Errors are checked before the loading check on purpose: a denial that
 * arrives before any snapshot has been delivered must resolve to `'denied'`,
 * never spin forever in `'loading'` — this is what avoids an infinite
 * spinner when a permission error is mistaken for "still waiting for
 * data".
 */
export function deriveChannelState(status: AdvisoryChannelStatus): ChannelState {
  if (status.errorCode === PERMISSION_DENIED_ERROR_CODE) {
    return 'denied';
  }
  if (status.errorCode !== null) {
    return 'failed';
  }
  if (!status.firstSnapshotReceived) {
    return 'loading';
  }
  return status.isEmpty ? 'empty' : 'ready';
}

/**
 * The engagement-classification wiring placeholder, recorded at its call
 * site (the goal document's `'confirmed'` branch of `deriveAdvisoryStage`,
 * below). Binding this exported const to the registry id makes the
 * reference compile-checked — deleting or renaming the
 * `WIRING_PLACEHOLDER_IDS.engagementClassification` entry breaks this
 * module's build, so the placeholder cannot silently outlive its record.
 */
export const STAGE_ENGAGEMENT_CLASSIFICATION_PLACEHOLDER =
  WIRING_PLACEHOLDER_IDS.engagementClassification;

/**
 * The confirmed project's analysis sub-stage, or plain `confirmed` when no run
 * has been prepared.
 *
 * Split out rather than inlined so the `confirmed` branch stays one line and this
 * mapping is readable as the table it is. Pure, like everything else here.
 *
 * A malformed or absent `analysis` resolves to `confirmed` — the same answer as
 * "no run yet". That is deliberate: this function's job is to say what stage the
 * project is in, and an unreadable sub-object is not evidence of a run. Inventing
 * a `running` stage from a document it could not parse would leave a UI waiting
 * on a run that may not exist.
 */
function deriveAnalysisStage(analysis: AnalysisDoc | null | undefined): AdvisoryStage {
  if (!analysis) {
    return { tag: 'confirmed' };
  }
  const runId = analysis.runId;
  switch (analysis.status) {
    case 'prepared':
      return { tag: 'analysisPrepared', docsCollectionId: analysis.docsCollectionId };
    case 'running':
      return {
        tag: 'analysisRunning',
        runId,
        plannedTurns: analysis.plannedTurns,
        completedTurns: analysis.completedTurns,
        failedTurns: analysis.failedTurns,
      };
    case 'completed':
      return {
        tag: 'analysisComplete',
        runId,
        completedTurns: analysis.completedTurns,
        plannedTurns: analysis.plannedTurns,
      };
    case 'partial':
      return {
        tag: 'analysisPartial',
        runId,
        completedTurns: analysis.completedTurns,
        failedTurns: analysis.failedTurns,
        plannedTurns: analysis.plannedTurns,
      };
    case 'analysis_failed':
      return { tag: 'analysisFailed', runId };
    default: {
      // Exhaustiveness guard: a sixth AnalysisDocStatus becomes a compile error
      // here rather than a silent fall-through to `confirmed`, which would show a
      // finished project while a run was in some state nobody mapped.
      const exhaustive: never = analysis.status;
      void exhaustive;
      return { tag: 'confirmed' };
    }
  }
}

/**
 * Derives the goal channel's stage.
 *
 * `channelState` and `goalDoc` are exactly this module's composition
 * contract: channel health is computed by `deriveChannelState` and fed in
 * here as the first argument, so exactly one place (`deriveChannelState`)
 * defines what "denied" means and the two definitions cannot drift.
 *
 * The optional third argument, `errorCode`, supplies the payload for the
 * `channelFailed` variant only — every `AdvisoryStage` variant carries its
 * required payload — when omitted or `null` it defaults to
 * `UNKNOWN_ADVISORY_ERROR_CODE`. The two-argument call,
 * `deriveAdvisoryStage(channelState, goalDoc)`, is the normal call shape;
 * the third argument exists solely to satisfy the `channelFailed` payload
 * requirement when a failure code is available.
 */
export function deriveAdvisoryStage(
  channelState: ChannelState,
  goalDoc: StrategicAdvisoryDoc | null,
  errorCode?: string | null,
  company?: CompanyFact | null,
  counterparties?: CounterpartiesFact | null,
  liveProfiles?: LiveCounterpartyProfiles | null,
  profilesAcknowledged?: boolean
): AdvisoryStage {
  // Channel health is branched on first, before any document field is read,
  // because a denied or failed channel means the document data cannot be
  // trusted. A `'denied'` channel returns `{ tag: 'denied' }` even when
  // `goalDoc` is non-null — cached data from a previously-permitted read
  // must never render after authorization was revoked.
  switch (channelState) {
    case 'loading':
      return { tag: 'loading' };
    case 'denied':
      return { tag: 'denied' };
    case 'failed':
      return { tag: 'channelFailed', errorCode: errorCode ?? UNKNOWN_ADVISORY_ERROR_CODE };
    case 'empty':
    case 'ready':
      // Both a healthy-but-empty and a healthy-with-data channel fall
      // through to the document branches below.
      break;
  }

  if (goalDoc === null) {
    // A healthy channel with no document is absent, never loading and
    // never an error.
    return { tag: 'absent' };
  }

  // Unified drafts own their company and period validation in the review fact.
  // Legacy fact gates must not interrupt this journey. Channel failures still win.
  if (goalDoc.status === 'review') return { tag: 'reviewing' };

  // The company, before ANY goal branch. It is read on the same first turn as
  // the goal (S-02a writes it just before the goal call), and the whole reason
  // it is asked immediately rather than at a step before the upload is that
  // everything after it is built on it: the engagement type, the question set,
  // and the subject every extracted figure is matched against. A correction
  // that arrives after the plan exists is a correction that discards the plan.
  //
  // Passed in rather than read off `goalDoc`: the company lives in the
  // `facts/company` subcollection, which the goal channel's listener never
  // sees. `undefined` means "not fetched yet" and `null` means "no such fact" —
  // both leave the stage where it was, so a project created before S-02a
  // existed is never gated and neither is one whose fetch is still in flight.
  if (company && !company.confirmed) {
    return {
      tag: 'awaitingCompanyConfirmation',
      companyName: company.companyName,
      extractionFailed: company.error.length > 0,
    };
  }

  // The counterparties, after the company and before every goal branch. Same
  // reasoning as the company gate, one step later: the engagement type is what
  // decides whether a counterparty exists, so this cannot be asked until the
  // goal is classified — and every key question of those two types is answered
  // against these names, so it must be asked before the set is locked.
  //
  // `null` (no such fact) covers the other nineteen engagement types and every
  // project that predates the stage; `undefined` covers a read still in flight.
  // Neither gates.
  if (counterparties && !counterparties.confirmed) {
    return {
      tag: 'awaitingCounterpartyConfirmation',
      companies: counterparties.companies,
      kind: counterparties.kind,
      extractionFailed: counterparties.error.length > 0,
    };
  }

  // The profiles, after the counterparty gate and before every goal branch.
  //
  // The research fires on the confirmation and the analysis WAITS for it, so
  // this stage is where that wait becomes visible. Without it the user sits on
  // a silent wizard for minutes per company and cannot tell work from a hang.
  //
  // It holds while any company is in flight, and keeps holding once they are
  // all finished IF any failed — a failure is the one case the user can still
  // act on, by supplying a URL and retrying. The screen offers its own way past
  // that, so this is not a dead end.
  //
  // Gated on the profiles map being NON-EMPTY, which is what keeps a project
  // confirmed before this stage existed out of it: such a project has no
  // records and never will, so gating on "no record yet" would strand it on a
  // research screen for research that is never coming. agent-svc writes
  // `researching` for a company BEFORE its job starts, so one record appears
  // almost immediately on a run that is really happening.
  //
  // A DROP also holds it, and holds it even when nothing is in flight and the
  // remaining list is clean: a company removed from the engagement for want of
  // evidence is a change to what the analysis will cover, and this screen is the
  // only place the user is told. Its `Continue` is always the way past.
  //
  // Opened by the fetched document OR by THIS tab's live stream. The stream is
  // what makes the stage appear at all during the run that matters: the fact is
  // read once at mount, before any profile exists, so a gate on the document
  // alone shows nothing until a reload — the user confirms the list and lands on
  // a screen with no rows, no progress and no retry.
  const liveRunning = Boolean(liveProfiles && liveProfiles.companies.length > 0);
  if (counterparties?.confirmed && (counterpartyStageHasRecords(counterparties) || liveRunning)) {
    const rows = counterpartyProfileRows(counterparties, liveProfiles);
    const pending = rows.some(row => row.status === null || row.status === 'researching');
    const failed = rows.some(
      row => row.status !== null && row.status !== 'researching' && row.status !== 'ingested'
    );
    // `profilesAcknowledged` is the screen's `Continue`, and it releases the
    // FAILURE hold only — never the `pending` one.
    //
    // The two halves of this gate hold for different reasons and so are
    // dismissable on different terms. `pending` is live work: releasing it would
    // drop the user past the only view of a run that is still going, and the
    // screen does not even offer `Continue` while it is true. A failure or a
    // drop is finished news about a permanent gap, so once the user has read it
    // and chosen to go on, re-deriving the same screen is what turns `Continue`
    // into a no-op — the click cleared the wizard params and then landed right
    // back here, because nothing about the fact had changed and nothing was
    // ever going to.
    //
    // Deliberately not persisted: it is this tab's acknowledgement of what it
    // showed, not a project state, and agent-svc has nowhere to put it. A reload
    // shows the gap again, which is the honest answer — the evidence is still
    // missing, and the reader may not be the person who dismissed it.
    if (pending || ((failed || counterparties.dropped.length > 0) && !profilesAcknowledged)) {
      return {
        tag: 'researchingCounterparties',
        kind: counterparties.kind || liveProfiles?.kind || '',
        rows,
        pending,
        dropped: counterparties.dropped,
      };
    }
  }

  switch (goalDoc.status) {
    case 'extracting':
      return { tag: 'extracting' };
    case 'failed':
      // Deliberately a different tag from 'denied' and from 'channelFailed'
      // — one generic error tag must never cover two different situations.
      // This is the goal doc's own extraction failure, not a channel-level
      // problem.
      return { tag: 'extractionFailed' };
    case 'awaiting_confirmation': {
      const attempt = goalDoc.goal.length;
      // The two clauses are equivalent for any document the mappers produce —
      // `canRefine` is itself derived as `goal.length < maxIterations`
      // (`advisoryService.ts`), because the backend persists no such field.
      // Both are kept deliberately: this function takes a
      // `StrategicAdvisoryDoc`, not a raw document, so a future caller
      // building one by hand must not be able to skip the cap check.
      if (!goalDoc.canRefine || attempt >= goalDoc.maxIterations) {
        return { tag: 'refinementExhausted', attempt, maxIterations: goalDoc.maxIterations };
      }
      return {
        tag: 'awaitingConfirmation',
        attempt,
        maxIterations: goalDoc.maxIterations,
        canRefine: true,
      };
    }
    case 'confirmed':
      // The analysis stage lives INSIDE this branch rather than beside it,
      // because agent-svc never moves the document's top-level `status` off
      // `confirmed` when a run starts — `begin_analysis` writes only the
      // `analysis` sub-object. A run in flight is therefore a confirmed project
      // with an `analysis`, and reading it here is what lets a reload mid-run
      // recover: an engagement is ~13 minutes on the debug shape and hours on a
      // full one, so the SSE stream is long gone by the time someone reloads,
      // and this document is the only thing that still says a run is going.
      //
      // No engagement-match stage follows: the backend WRITE is now wired (it
      // populates the document's `engagement`), but the client PANEL that would
      // render it is v2-deferred — see WP-06 in the placeholder registry, and
      // STAGE_ENGAGEMENT_CLASSIFICATION_PLACEHOLDER above. The fork overlay
      // (express vs customize) still belongs to a wider function than this
      // narrow goal-channel one.
      return deriveAnalysisStage(goalDoc.analysis);
    default: {
      // Exhaustiveness guard: a future GoalDocStatus member becomes a
      // compile error here rather than a silent `undefined` return.
      const exhaustive: never = goalDoc.status;
      return exhaustive;
    }
  }
}

/**
 * One row per CONFIRMED counterparty, in the order the user curated them.
 *
 * Driven by `companies` rather than by the keys of `profiles`, which is what
 * makes "not reached yet" representable: a company with no record gets a row
 * with `status: null`. Iterating the profile map instead would silently omit
 * exactly the companies whose research has not started, and the screen would
 * show a short list that looks complete.
 */
/**
 * Whether this project's counterparty research left any record at all.
 *
 * The guard that keeps a project confirmed BEFORE this stage existed out of the
 * research screen: it has no profiles and no receipts and never will, so gating
 * on "no record yet" would strand it waiting for research that is never coming.
 * agent-svc writes `researching` for a company before its job starts, so one
 * record appears almost immediately on a run that is really happening.
 *
 * A receipt counts as a record on its own, which is what covers the engagement
 * whose every counterparty was dropped: `companies` is then empty and no profile
 * survives, and without this the user would never be told why.
 */
function counterpartyStageHasRecords(fact: CounterpartiesFact): boolean {
  return Object.keys(fact.profiles).length > 0 || fact.dropped.length > 0;
}

export function counterpartyProfileRows(
  fact: CounterpartiesFact,
  live?: LiveCounterpartyProfiles | null
): CounterpartyProfileRow[] {
  // The confirmed list, plus any company the live run names that the fetched
  // document has not caught up with — a confirm turn writes the list and starts
  // the research in one stream, so the mount-time read can predate both.
  const companies = [...fact.companies];
  for (const company of live?.companies ?? []) {
    if (!companies.includes(company)) {
      companies.push(company);
    }
  }
  // A company dropped mid-run is gone from the engagement; the receipt is what
  // the screen shows instead, so leaving a row would contradict it.
  const droppedLive = new Set(live?.dropped ?? []);

  return companies
    .filter(company => !droppedLive.has(company))
    .map(company => {
      const record = fact.profiles[company];
      // Live wins over the document: both describe the same run, and the stream
      // is ahead of a read taken before it started. `sourceUrl` is the
      // exception — it is only ever in the document, because it is what the
      // user typed on a previous attempt.
      const settled = live?.settled[company];
      const inFlight = live?.started.includes(company) ?? false;
      const status = settled?.status ?? (inFlight ? 'researching' : record?.status ?? null);
      return {
        company,
        status,
        fileName: settled?.fileName || record?.fileName || '',
        chars: settled?.chars ?? record?.chars ?? 0,
        detail: settled?.detail || record?.detail || '',
        sourceUrl: record?.sourceUrl ?? '',
      };
    });
}
